import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../ui/utils';
import { bandStyle, overallContributions, CONFIDENCE_LABEL, type OrganScore } from './organHealth';

/**
 * The total-body score's derivation. Unlike a single organ (a lab → curve →
 * cap chain), the Overall is a roll-up: it is the *weakest* graded organ, never
 * a mean (scorer.compute_overall_score). This shows every graded organ with its
 * score + band, marks the anchor (the weakest), and states the two rules that
 * produced the number — score = min, confidence = lowest among graded.
 *
 * Built only from persisted structured_fields (organ_scores / anchored_on);
 * renders nothing when the backend didn't send the breakdown.
 */
export function OverallProvenance({ organ }: { organ: OrganScore }) {
  const [open, setOpen] = useState(false);
  const rows = overallContributions(organ);
  if (rows.length === 0) return null;

  return (
    <div className="border-t border-border-subtle pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-xs font-medium text-brand-text hover:underline"
      >
        {open ? <ChevronDown className="size-3.5" aria-hidden /> : <ChevronRight className="size-3.5" aria-hidden />}
        {open ? 'Hide how this rolls up' : 'Show how this rolls up'}
      </button>
      {open ? (
        <div className="mt-2 overflow-hidden rounded-lg border border-border-subtle bg-surface-sunken/20">
          <div className="border-b border-border-subtle px-3 py-1.5">
            <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-text-tertiary">
              How the total rolls up
            </span>
          </div>
          <ul>
            {rows.map((r, i) => {
              const s = bandStyle(r.band);
              const Icon = s.icon;
              return (
                <li
                  key={r.organ}
                  className={cn(
                    'flex items-center justify-between gap-3 px-3 py-2',
                    i > 0 && 'border-t border-border-subtle',
                    r.isAnchor && 'border-l-2 border-l-(--capped-rule) bg-surface-sunken/50',
                  )}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="text-[13px] font-medium text-text-primary">{r.label}</span>
                    {r.isAnchor ? (
                      <span className="rounded-sm bg-band-severe/10 px-1.5 text-[10px] font-medium uppercase tracking-wide text-band-severe-text">
                        anchors overall
                      </span>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2.5">
                    <span className={cn('inline-flex items-center gap-1 text-xs font-medium', s.accent)}>
                      <Icon className="size-3" aria-hidden />
                      {s.glyph ? <span aria-hidden>{s.glyph}</span> : null}
                      {s.label}
                    </span>
                    <span className="w-8 text-right text-sm font-medium tabular-nums text-text-primary">{r.score}</span>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="space-y-1 border-t border-border-subtle bg-surface-sunken/30 px-3 py-2 text-xs leading-relaxed text-text-secondary">
            <p>
              <span className="font-medium text-text-primary">Score = the weakest organ</span> — the body is only as
              healthy as its most-affected graded organ, so the overall is the minimum, never an average.
            </p>
            <p>
              <span className="font-medium text-text-primary">
                Confidence = {CONFIDENCE_LABEL[organ.confidence].replace(' confidence', '').toLowerCase()}
              </span>{' '}
              — the lowest among the graded organs, kept conservative.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
